/**
 * <bk-endpoint-filter> — client-side filtering of the resource page's endpoint rows
 * (markup: sections/developer-resource-endpoints.liquid). Every row is server-rendered; this
 * only hides/shows them, so the page works without JS.
 *
 * Behaviour = the design's renderVals() (docs/design/pages/resource):
 * - text needle = filter value trimmed + lower-cased; a row matches when "path summary" contains
 *   every word of it, in any order;
 * - method pills: counts follow the text match; a method with no matches is disabled unless it
 *   is the pressed one; aria-pressed marks the current method (ALL by default);
 * - each group shows its visible count, its list or its "No … match these filters." note;
 *   a group with no rows at all stays hidden (the server renders it hidden; nothing to filter);
 * - no visible rows at all → the no-results panel (Clear filters / Search all endpoints);
 * - count line: "N endpoints", or "Showing X of N endpoints" while filtered.
 * The "Search all endpoints" link carries the current text as ?q=.
 */
const plural = (n, one, many) => (n === 1 ? one : many).replace('[count]', String(n));

class BkEndpointFilter extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.method = 'ALL';
    this.input = this.querySelector('#ep-filter');
    this.countLine = this.querySelector('#ep-count');
    this.pills = Array.from(this.querySelectorAll('.bk-pill[data-method]'));
    this.results = this.querySelector('[data-ep-results]');
    this.none = this.querySelector('[data-ep-none]');
    this.searchLink = this.querySelector('[data-ep-search]');
    this.searchHref = this.searchLink ? this.searchLink.getAttribute('href') : '';

    this.groups = Array.from(this.querySelectorAll('[data-ep-group]')).map((el) => ({
      el,
      count: el.querySelector('[data-ep-group-count]'),
      list: el.querySelector('[data-ep-list]'),
      empty: el.querySelector('[data-ep-empty]'),
      rows: Array.from(el.querySelectorAll('.bk-op')).map((li) => {
        const badge = li.querySelector('.bk-m');
        const code = li.querySelector('code');
        const sum = li.querySelector('.bk-op-sum');
        return {
          li,
          method: badge ? badge.textContent.trim() : '',
          text: ((code ? code.textContent : '') + ' ' + (sum ? sum.textContent : '')).toLowerCase()
        };
      })
    }));
    this.total = this.groups.reduce((n, g) => n + g.rows.length, 0);

    if (this.input) {
      this.input.addEventListener('input', () => this.update());
      // A type="search" field submits nothing here; Enter must not reload the page.
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
    }
    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        this.method = pill.dataset.method;
        this.update();
      });
    });
    const clear = this.querySelector('[data-ep-clear]');
    if (clear) {
      clear.addEventListener('click', () => {
        if (this.input) this.input.value = '';
        this.method = 'ALL';
        this.update();
        if (this.input) this.input.focus();
      });
    }
    // The browser may restore a typed value on back/forward navigation.
    if (this.input && this.input.value) this.update();
  }

  update() {
    const query = this.input ? this.input.value : '';
    const needle = query.trim().toLowerCase();
    const method = this.method;
    const counts = { ALL: 0 };
    let visibleTotal = 0;

    for (const group of this.groups) {
      let visible = 0;
      for (const row of group.rows) {
        // Every word of the filter, in any order ("update vendor" finds "Update several vendors").
        const textMatch = needle === '' || needle.split(/\s+/).every((w) => row.text.indexOf(w) !== -1);
        if (textMatch) {
          counts.ALL += 1;
          counts[row.method] = (counts[row.method] || 0) + 1;
        }
        const show = textMatch && (method === 'ALL' || row.method === method);
        row.li.hidden = !show;
        if (show) visible += 1;
      }
      visibleTotal += visible;
      group.el.hidden = group.rows.length === 0;
      if (group.count) group.count.textContent = String(visible);
      if (group.list) group.list.hidden = visible === 0;
      if (group.empty) group.empty.hidden = visible !== 0;
    }

    for (const pill of this.pills) {
      const m = pill.dataset.method;
      const count = counts[m] || 0;
      pill.setAttribute('aria-pressed', m === method ? 'true' : 'false');
      pill.disabled = m !== 'ALL' && count === 0 && m !== method;
      const n = pill.querySelector('.bk-pill-n');
      if (n) n.textContent = String(count);
    }

    if (this.results) this.results.hidden = visibleTotal === 0;
    if (this.none) this.none.hidden = visibleTotal !== 0;

    const all = plural(this.total, this.dataset.countSingular || '[count] endpoint', this.dataset.countPlural || '[count] endpoints');
    const filtered = needle !== '' || method !== 'ALL';
    if (this.countLine) {
      this.countLine.textContent = filtered
        ? (this.dataset.countFiltered || 'Showing [shown] of [total]').replace('[shown]', String(visibleTotal)).replace('[total]', all)
        : all;
    }

    if (this.searchLink && this.searchHref) {
      const term = query.trim();
      this.searchLink.setAttribute('href', term ? this.searchHref + (this.searchHref.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(term) : this.searchHref);
    }
  }
}

if (!customElements.get('bk-endpoint-filter')) customElements.define('bk-endpoint-filter', BkEndpointFilter);
